import FormSwitch from "@babackapp/react-hook-forms-components/form-switch";
import useTranslation from "@babackapp/translations/use-translation";
import { Button } from "@babackapp/ui/components/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@babackapp/ui/components/command";
import { FormField } from "@babackapp/ui/components/form";
import { Input } from "@babackapp/ui/components/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@babackapp/ui/components/popover";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@babackapp/ui/components/tooltip";
import { Switch } from "@babackapp/ui/components/switch";
import { useCarriersQuery } from "@babackapp/private-api-client/client/use-carriers-query";
import {
  ChevronsUpDownIcon,
  Loader2Icon,
  PlusIcon,
  Trash2Icon,
} from "lucide-react";
import { useState } from "react";
import { Controller, useFieldArray, useFormContext } from "react-hook-form";
import { useCurrentShopSuspense } from "@/api/shops/use-current-shop-suspense";
import { useViewerHasAtLeastAdminRights } from "@/lib/rights/use-viewer-has-right";
import {
  ALL_CURRENCIES,
  ALL_CURRENCIES_BY_CODE,
  COMMON_CURRENCY_CODES,
  countryToFlag,
  type CurrencyInfo,
} from "./currencies";
import type { ShopSettingsFormValues } from "./types";

// ─── CurrencyCombobox ────────────────────────────────────────────────────────

function CurrencyCombobox({
  value,
  onChange,
  carrierCurrencies,
  isLoadingCarriers,
  alreadyConfigured,
  hasError,
  disabled,
}: {
  value: string;
  onChange: (code: string) => void;
  carrierCurrencies: string[];
  isLoadingCarriers?: boolean;
  alreadyConfigured: string[];
  hasError?: boolean;
  disabled?: boolean;
}) {
  const t = useTranslation();
  const [open, setOpen] = useState(false);

  // Other rows' configured currencies (current row's own value is allowed)
  const otherConfigured = alreadyConfigured.filter((c) => c !== value);

  const visibleCarrier: CurrencyInfo[] = carrierCurrencies
    .filter((code) => !otherConfigured.includes(code))
    .map((code) => ALL_CURRENCIES_BY_CODE.get(code))
    .filter(Boolean) as CurrencyInfo[];

  const carrierCodes = new Set(visibleCarrier.map((c) => c.code));

  const commonCurrencies: CurrencyInfo[] = COMMON_CURRENCY_CODES.filter(
    (code) => !carrierCodes.has(code) && !otherConfigured.includes(code),
  )
    .map((code) => ALL_CURRENCIES_BY_CODE.get(code))
    .filter(Boolean) as CurrencyInfo[];

  const commonCodes = new Set([
    ...carrierCodes,
    ...commonCurrencies.map((c) => c.code),
  ]);

  const otherCurrencies: CurrencyInfo[] = ALL_CURRENCIES.filter(
    (c) => !commonCodes.has(c.code) && !otherConfigured.includes(c.code),
  );

  const currentInfo = value ? ALL_CURRENCIES_BY_CODE.get(value) : undefined;

  const renderItem = ({ code, name, country }: CurrencyInfo) => (
    <CommandItem
      key={code}
      value={`${code} ${name}`}
      onSelect={() => {
        onChange(code);
        setOpen(false);
      }}
    >
      <span className="mr-1.5 text-base">{countryToFlag(country)}</span>
      <span className="font-mono font-medium w-10 shrink-0">{code}</span>
      <span className="text-muted-foreground truncate text-xs">— {name}</span>
    </CommandItem>
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          disabled={disabled}
          className={`h-8 w-full justify-between px-2 text-sm font-normal${hasError ? " border-destructive ring-1 ring-destructive" : ""}`}
        >
          {currentInfo ? (
            <span className="flex items-center gap-1.5 truncate">
              <span>{countryToFlag(currentInfo.country)}</span>
              <span className="font-mono font-medium">{currentInfo.code}</span>
              <span className="text-muted-foreground text-xs truncate">
                — {currentInfo.name}
              </span>
            </span>
          ) : (
            <span className="text-muted-foreground">—</span>
          )}
          <ChevronsUpDownIcon className="ml-1 size-3.5 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-64 p-0" align="start">
        <Command>
          <CommandInput
            placeholder={t(
              "settings.shop.marketConversionRates.currencySearch",
            )}
          />
          <CommandList>
            <CommandEmpty>
              {t("settings.shop.marketConversionRates.noResults")}
            </CommandEmpty>

            {isLoadingCarriers && (
              <CommandGroup
                heading={t("settings.shop.marketConversionRates.carriersGroup")}
              >
                <CommandItem disabled>
                  <Loader2Icon className="size-3 animate-spin mr-1.5 text-muted-foreground" />
                  <span className="text-muted-foreground text-xs">…</span>
                </CommandItem>
              </CommandGroup>
            )}

            {!isLoadingCarriers && visibleCarrier.length > 0 && (
              <CommandGroup
                heading={t("settings.shop.marketConversionRates.carriersGroup")}
              >
                {visibleCarrier.map(renderItem)}
              </CommandGroup>
            )}

            {!isLoadingCarriers && visibleCarrier.length > 0 && (
              <CommandSeparator />
            )}

            <CommandGroup
              heading={t("settings.shop.marketConversionRates.commonGroup")}
            >
              {commonCurrencies.map(renderItem)}
            </CommandGroup>

            {otherCurrencies.length > 0 && <CommandSeparator />}

            {otherCurrencies.length > 0 && (
              <CommandGroup
                heading={t("settings.shop.marketConversionRates.otherGroup")}
              >
                {otherCurrencies.map(renderItem)}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

// ─── RateInputTooltip ────────────────────────────────────────────────────────

function RateInputTooltip({
  rate,
  shopCurrency,
  customerCurrency,
  children,
}: {
  rate: number;
  shopCurrency: string;
  customerCurrency: string;
  children: React.ReactNode;
}) {
  const t = useTranslation();

  if (!customerCurrency || !rate || rate <= 0) return <>{children}</>;

  const result = (100 * rate).toFixed(2);

  return (
    <Tooltip delayDuration={150}>
      <TooltipContent className="text-black bg-white border-input border w-64 p-3">
        <p className="text-sm font-medium mb-2">
          1 {shopCurrency} = {rate} {customerCurrency}
        </p>
        <p className="text-xs text-gray-500">
          {t("settings.shop.marketConversionRates.fields.rateTooltipExample", {
            shopCurrency,
            result,
            customerCurrency,
          })}
        </p>
      </TooltipContent>
      <TooltipTrigger asChild>{children}</TooltipTrigger>
    </Tooltip>
  );
}

// ─── MarketConversionRatesForm ───────────────────────────────────────────────

export default function MarketConversionRatesForm() {
  const t = useTranslation();
  const { data: shop } = useCurrentShopSuspense();
  const { control, watch } = useFormContext<ShopSettingsFormValues>();
  const isAdmin = useViewerHasAtLeastAdminRights();

  const isEnabled = watch("marketConversionRates.enabled");

  const { fields, append, remove } = useFieldArray({
    control,
    name: "marketConversionRates.rates",
  });

  const { data: carriers, isLoading: isLoadingCarriers } = useCarriersQuery(
    shop.id,
  );
  const carrierCurrencies = [
    ...new Set(
      (carriers ?? []).flatMap((c) => c.pricings.map((p) => p.currency)),
    ),
  ];

  const shopCurrency = shop.preferences.currency;
  const ratesValues = watch("marketConversionRates.rates");

  const configuredCurrencies = [
    shopCurrency,
    ...fields.map((f) => f.currencyCode).filter(Boolean),
  ];

  return (
    <>
      <FormField
        control={control}
        name="marketConversionRates.enabled"
        render={({ field }) => (
          <FormSwitch
            label={t("settings.shop.marketConversionRates.title")}
            description={t("settings.shop.marketConversionRates.description", {
              currency: shop.preferences.currency,
            })}
            disabled={!isAdmin}
            {...field}
          />
        )}
      />

      {isEnabled && (
        <div className="mt-2 rounded-md border border-border overflow-hidden">
          <div className="grid grid-cols-[1fr_180px_80px_36px] items-center px-3 py-2 bg-muted/40 border-b border-border">
            <span className="text-xs font-medium text-muted-foreground">
              {t("settings.shop.marketConversionRates.fields.currencyCode")}
            </span>
            <span className="text-xs font-medium text-muted-foreground">
              {t("settings.shop.marketConversionRates.fields.rate", {
                currency: shopCurrency,
              })}
            </span>
            <span className="text-xs font-medium text-muted-foreground">
              {t("settings.shop.marketConversionRates.fields.enabled")}
            </span>
            <span />
          </div>

          {fields.length > 0 && (
            <div className="divide-y divide-border">
              {fields.map((field, index) => (
                <div
                  key={field.id}
                  className="grid grid-cols-[1fr_180px_80px_36px] items-center gap-0 px-3 py-2"
                >
                  <Controller
                    control={control}
                    name={`marketConversionRates.rates.${index}.currencyCode`}
                    render={({ field: f }) => (
                      <CurrencyCombobox
                        value={f.value}
                        onChange={f.onChange}
                        carrierCurrencies={carrierCurrencies}
                        isLoadingCarriers={isLoadingCarriers}
                        alreadyConfigured={configuredCurrencies}
                        hasError={!f.value}
                        disabled={!isAdmin}
                      />
                    )}
                  />
                  <Controller
                    control={control}
                    name={`marketConversionRates.rates.${index}.rate`}
                    render={({ field: f, fieldState }) => (
                      <RateInputTooltip
                        rate={ratesValues[index]?.rate}
                        shopCurrency={shopCurrency}
                        customerCurrency={ratesValues[index]?.currencyCode}
                      >
                        <Input
                          type="number"
                          step="any"
                          min="0.0001"
                          max="10000"
                          disabled={!isAdmin}
                          className={`h-8 w-32 text-sm [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none${fieldState.error ? " border-destructive ring-1 ring-destructive" : ""}`}
                          value={f.value}
                          onChange={(e) => {
                            f.onChange(e.target.valueAsNumber);
                          }}
                          onBlur={f.onBlur}
                        />
                      </RateInputTooltip>
                    )}
                  />
                  <Controller
                    control={control}
                    name={`marketConversionRates.rates.${index}.enabled`}
                    render={({ field: f }) => (
                      <Switch
                        checked={f.value}
                        onCheckedChange={f.onChange}
                        disabled={!isAdmin}
                      />
                    )}
                  />
                  <button
                    type="button"
                    disabled={!isAdmin}
                    onClick={() => remove(index)}
                    className="flex items-center justify-center size-8 rounded text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors disabled:pointer-events-none disabled:opacity-50"
                  >
                    <Trash2Icon className="size-3.5" />
                  </button>
                </div>
              ))}
            </div>
          )}

          <div className="px-3 py-2 border-t border-border bg-muted/20">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              disabled={!isAdmin}
              onClick={() =>
                append({ currencyCode: "", rate: 1, enabled: true })
              }
            >
              <PlusIcon className="size-3.5 mr-1.5" />
              {t("settings.shop.marketConversionRates.addRate")}
            </Button>
          </div>
        </div>
      )}
    </>
  );
}
